// The write state both agent-config surfaces share — `/account/templates` and
// `/account/prompt-vars`. Split from the routes the way `createMachineRemove` is:
// a form with a re-entrancy guard, an inline refusal, and a separate destructive
// confirm is STATE, not rendering. Per-surface, never a global store.
//
// Two consumers, one state machine — not a speculative primitive. Both resources
// are upsert-by-address with an idempotent delete, so both need exactly: which
// row the form is authoring, whether a write is in flight, the API's refusal for
// THAT form, and a confirm step whose own failure must not be swallowed.
//
// Two error signals on purpose. `error()` belongs to the open form (a 422 naming
// the field to fix); `deleteError()` belongs to the confirm dialog, which has no
// inline slot and renders through a toast. One signal for both would paint a
// delete failure inside a create form.

import { createSignal } from 'solid-js';
import { errorReason } from './api';

/** What the form is authoring: a brand-new row, an existing one, or nothing. */
export type EditTarget<T> = T | 'new' | null;

export type AgentConfigEditor<T> = {
  /** The row the form is editing, `'new'` for a create, or null when closed. */
  readonly editing: () => EditTarget<T>;
  /** The API's refusal for the open form, or null. Rendered inline. */
  readonly error: () => string | null;
  /** True while a write is in flight — disables the submit. */
  readonly pending: () => boolean;
  /** Open the form. No argument = create; a row = edit it. Clears any refusal. */
  readonly open: (row?: T) => void;
  /** Dismiss the form without writing. */
  readonly close: () => void;
  /**
   * Run one write. A second call while one is in flight is dropped. On success
   * the form closes and `onWritten` fires — the caller RE-READS; nothing here
   * patches a row from a write's echo.
   */
  readonly submit: (write: () => Promise<unknown>) => void;
  /** The row awaiting delete confirmation, or null. */
  readonly deleting: () => T | null;
  /** Failure copy for the confirm's toast, or null. */
  readonly deleteError: () => string | null;
  /** Open the delete confirm for a row, clearing any previous failure. */
  readonly askDelete: (row: T) => void;
  /** Dismiss the confirm without deleting. */
  readonly cancelDelete: () => void;
  /** Clear the delete failure — wire to the toast's `onDismiss`. */
  readonly clearDeleteError: () => void;
  /** Delete the row under confirmation. Dropped while one is in flight. */
  readonly confirmDelete: (write: (row: T) => Promise<unknown>) => void;
};

/**
 * Editor state for one surface. `fallback` is the copy shown when a failure
 * carries no RFC 9457 `detail` — the API's own sentence wins whenever it sent
 * one, because it names the field (an unpaired backend, a secret-shaped value)
 * and a paraphrase here would drift from what the other two doors answer.
 */
export function createAgentConfigEditor<T>(input: {
  readonly fallback: string;
  readonly onWritten: () => void;
}): AgentConfigEditor<T> {
  const [editing, setEditing] = createSignal<EditTarget<T>>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [pending, setPending] = createSignal(false);
  const [deleting, setDeleting] = createSignal<T | null>(null);
  const [deleteError, setDeleteError] = createSignal<string | null>(null);
  // Busy guard rather than a disabled action button — ConfirmDialog has none.
  let deleteBusy = false;

  const submit = (write: () => Promise<unknown>): void => {
    if (pending()) return;
    setPending(true);
    setError(null);
    void write()
      .then(() => {
        setEditing(null);
        input.onWritten();
      })
      .catch((e: unknown) => setError(errorReason(e, input.fallback)))
      .finally(() => setPending(false));
  };

  const confirmDelete = (write: (row: T) => Promise<unknown>): void => {
    const row = deleting();
    if (row === null || deleteBusy) return;
    deleteBusy = true;
    setDeleteError(null);
    void write(row)
      .then(() => {
        // Close only the confirm THIS call opened: nothing stops the reader
        // dismissing it and asking about a different row mid-flight, and a blind
        // clear would dismiss that newer dialog out from under them.
        if (deleting() === row) setDeleting(null);
        input.onWritten();
      })
      .catch((e: unknown) => setDeleteError(errorReason(e, input.fallback)))
      .finally(() => {
        deleteBusy = false;
      });
  };

  return {
    editing,
    error,
    pending,
    open: (row?: T) => {
      setError(null);
      setEditing(() => row ?? 'new');
    },
    close: () => setEditing(null),
    submit,
    deleting,
    deleteError,
    askDelete: (row: T) => {
      setDeleteError(null);
      setDeleting(() => row);
    },
    cancelDelete: () => setDeleting(null),
    clearDeleteError: () => setDeleteError(null),
    confirmDelete,
  };
}
